
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const VictorianADUDesignSaratoga = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Victorian ADU Design & Construction in Saratoga",
    "description": "Custom Victorian ADUs in Saratoga. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/victorian-adu-design-saratoga"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/victorian-adu-design-saratoga" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Victorian ADU Design & Construction in Saratoga
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Exquisite Victorian accessory dwelling units designed for Saratoga's luxury estates and historic character.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Victorian ADUs in Saratoga?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Estate Elegance</h3>
              <p className="text-gray-300">Victorian design complements Saratoga's luxury estate properties with sophisticated architectural details.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Historic Charm</h3>
              <p className="text-gray-300">Authentic Victorian elements that honor Saratoga's rich history and architectural heritage.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Premium Investment</h3>
              <p className="text-gray-300">Victorian ADUs add exceptional value to Saratoga's exclusive real estate market.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Victorian ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Intricate woodwork and decorative trim details</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Asymmetrical facades with bay windows</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Ornate porches with decorative columns</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Multi-textured exterior materials and colors</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Period-appropriate interior finishes and fixtures</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Saratoga-Specific Victorian ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              In Saratoga, Victorian ADUs must comply with specific local requirements:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Architectural review for compatibility with estate properties</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Large lot setbacks and privacy considerations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Oak tree preservation and environmental sensitivity</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>High-end construction standards and materials</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our experienced team navigates Saratoga's unique requirements for exceptional Victorian ADU projects.
            </p>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Victorian ADU Cost in Saratoga
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Victorian ADU in Saratoga?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Let's create a stunning Victorian ADU that enhances your Saratoga estate.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Saratoga:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/saratoga-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Saratoga
              </Link>
              <Link to="/adu-cost-saratoga" className="text-[#FF7A00] hover:underline">
                ADU Cost in Saratoga
              </Link>
              <Link to="/adu-regulations-saratoga" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Saratoga
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Victorian ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/victorian-adu-design-san-jose" className="text-[#FF7A00] hover:underline">
                Victorian ADU San Jose
              </Link>
              <Link to="/victorian-adu-design-palo-alto" className="text-[#FF7A00] hover:underline">
                Victorian ADU Palo Alto
              </Link>
              <Link to="/victorian-adu-design-los-altos" className="text-[#FF7A00] hover:underline">
                Victorian ADU Los Altos
              </Link>
              <Link to="/victorian-adu-design-mountain-view" className="text-[#FF7A00] hover:underline">
                Victorian ADU Mountain View
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default VictorianADUDesignSaratoga;
